import React, { useState, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  Users, 
  BarChart3, 
  PanelLeftClose,
  PanelRightClose,
  Bell,
  Sun,
  Moon,
  FolderOpen,
  UserCheck,
  Wallet,
  DollarSign
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toggleSidebar, setTheme } from '../../store/slices/uiSlice';
import clsx from 'clsx';
import NotificationDropdown from '../UI/NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import RoleSwitcher from '../UI/RoleSwitcher';
import { Role } from '../../hooks/useRoleSwitching';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  roles: ('admin' | 'host' | 'guest' | 'usher')[];
  badge?: string;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: 'dashboard', icon: Home, roles: ['admin', 'host', 'guest'] },
  { name: 'Events', href: 'events', icon: Calendar, roles: ['admin', 'host'] },
  { name: 'Manage Categories', href: 'categories', icon: FolderOpen, roles: ['admin'] },
  { name: 'User Management', href: 'users', icon: Users, roles: ['admin'] },
  { name: 'Host Balances', href: 'billing/balances', icon: DollarSign, roles: ['admin'] },
  { name: 'My phone book', href: 'guests', icon: UserCheck, roles: ['host'] },
  { name: 'My Balance', href: 'billing', icon: Wallet, roles: ['host'] },
  { name: 'Analytics', href: 'analytics', icon: BarChart3, roles: ['admin', 'host'] },
  { name: 'My Analytics', href: 'analytics', icon: BarChart3, roles: ['guest'] },
];

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { sidebarOpen, theme } = useAppSelector((state) => state.ui);
  const location = useLocation();

  const userRole = user?.role || 'guest';
  const filteredNavigation = navigation.filter(item => item.roles.includes(userRole));

  // Mobile sidebar notification state
  const [showNotifications, setShowNotifications] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const { unreadCount } = useNotifications({ autoFetch: true });

  const handleNotificationClick = () => {
    if (notificationButtonRef.current) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowNotifications(!showNotifications);
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    dispatch(setTheme(newTheme));
  };

  const getBasePath = () => {
    return `/${userRole}`;
  };

  return (
    <>
      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => dispatch(toggleSidebar())}
        />
      )}

      {/* Desktop Sidebar */}
      <div 
        className={clsx(
          'fixed top-16 bottom-0 left-0 z-40 flex flex-col border-r transition-all duration-300 ease-in-out',
          sidebarOpen ? 'w-60' : 'w-16',
          'hidden lg:flex',
          theme === 'dark' 
            ? 'bg-gray-900 border-gray-700/50' 
            : 'bg-white border-gray-200/50'
        )}
      >
        {/* Sidebar Toggle Button */}
        <div className="flex items-center px-2 py-2">
          <button
            onClick={() => dispatch(toggleSidebar())}
            className={clsx(
              'p-2 rounded-lg transition-all duration-200',
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
            )}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? (
              <PanelLeftClose className="h-5 w-5" />
            ) : (
              <PanelRightClose className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Navigation - Top aligned */}
        <nav className="flex-1 px-2 py-2 space-y-1">
          {filteredNavigation.map((item) => {
            // Check if current path matches or starts with the item href (for nested routes)
            const itemPath = `${getBasePath()}/${item.href}`;
            const isActive = location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
            
            return (
              <NavLink
                key={item.name}
                to={`${getBasePath()}/${item.href}`}
                className={clsx(
                  'group flex items-center text-sm font-medium rounded-md transition-all duration-200 relative',
                  sidebarOpen ? 'px-2.5 py-2.5' : 'px-2 py-2',
                  isActive
                    ? theme === 'dark'
                      ? 'bg-[#33FF00]/15 text-[#33FF00]'
                      : 'bg-primary-50 text-primary-700'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
                      : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'
                )}
                title={!sidebarOpen ? item.name : undefined}
              >
                <item.icon
                  className={clsx(
                    'flex-shrink-0 h-5 w-5',
                    sidebarOpen ? 'mr-2.5' : '',
                    isActive 
                      ? theme === 'dark' ? 'text-[#33FF00]' : 'text-primary-600'
                      : theme === 'dark' ? 'text-gray-400 group-hover:text-gray-200' : 'text-gray-500 group-hover:text-gray-700'
                  )}
                />
                {sidebarOpen && (
                  <span className="truncate whitespace-nowrap overflow-hidden animate-fade-in">
                    {item.name}
                  </span>
                )}
                {sidebarOpen && item.badge && (
                  <span className="ml-auto bg-[#33FF00] text-black text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px] animate-fade-in">
                    {item.badge}
                  </span>
                )}
                {!sidebarOpen && item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#33FF00] text-black text-xs rounded-full h-3.5 w-3.5 flex items-center justify-center text-[10px]">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Mobile Sidebar */}
      <div 
        data-mobile-sidebar
        className={clsx(
          'fixed top-14 sm:top-16 bottom-0 left-0 z-40 flex flex-col w-60 border-r transform transition-transform duration-300 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          theme === 'dark' 
            ? 'bg-gray-900 border-gray-700/50' 
            : 'bg-white border-gray-200/50'
        )}
      >
        {/* Navigation - Top aligned */}
        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {filteredNavigation.map((item) => {
            // Check if current path matches or starts with the item href (for nested routes)
            const itemPath = `${getBasePath()}/${item.href}`;
            const isActive = location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
            
            return (
              <NavLink
                key={item.name}
                to={`${getBasePath()}/${item.href}`}
                onClick={() => dispatch(toggleSidebar())}
                className={clsx(
                  'group flex items-center px-2.5 py-2.5 text-sm font-medium rounded-md transition-colors duration-200',
                  isActive
                    ? theme === 'dark'
                      ? 'bg-[#33FF00]/15 text-[#33FF00]'
                      : 'bg-primary-50 text-primary-700'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
                      : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'
                )}
              >
                <item.icon
                  className={clsx(
                    'mr-2.5 flex-shrink-0 h-5 w-5',
                    isActive 
                      ? theme === 'dark' ? 'text-[#33FF00]' : 'text-primary-600'
                      : theme === 'dark' ? 'text-gray-400 group-hover:text-gray-200' : 'text-gray-500 group-hover:text-gray-700'
                  )}
                />
                {item.name}
                {item.badge && (
                  <span className="ml-auto bg-[#33FF00] text-black text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* Mobile-only: Role Switcher, Theme Toggle and Notifications */}
          <div className={clsx(
            'lg:hidden pt-4 mt-4 space-y-2',
            theme === 'dark' ? 'border-t border-gray-700' : 'border-t border-gray-200'
          )}>
            {/* Role Switcher - Show if user has multiple roles */}
            {user && user.roles && user.roles.length > 1 && (
              <div className="w-full">
                <RoleSwitcher 
                  currentRole={(user.active_role || user.role || 'guest') as Role} 
                  fullWidth={true}
                />
              </div>
            )}

            {/* Theme Toggle */}
            <button
              onClick={handleThemeToggle}
              className={clsx(
                'w-full group flex items-center px-2.5 py-2.5 text-sm font-medium rounded-md transition-colors duration-200',
                theme === 'dark'
                  ? 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
                  : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'
              )}
            >
              {theme === 'light' ? (
                <Moon className="mr-2.5 flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-200" />
              ) : (
                <Sun className="mr-2.5 flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-200" />
              )}
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>

            {/* Notifications - Hidden for guests */}
            {(user?.role !== 'guest' && user?.active_role !== 'guest') && (
              <div className="relative">
                <button
                  ref={notificationButtonRef}
                  onClick={handleNotificationClick}
                  className={clsx(
                    'w-full group flex items-center px-2.5 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 relative',
                    theme === 'dark'
                      ? 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
                      : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'
                  )}
                >
                  <Bell className="mr-2.5 flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-200" />
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications dropdown */}
                <NotificationDropdown
                  isOpen={showNotifications}
                  onClose={() => setShowNotifications(false)}
                  position={dropdownPosition}
                  theme={theme}
                  triggerRef={notificationButtonRef}
                />
              </div>
            )}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
